import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";

const STORAGE_KEY = "sp-last-purchase";

/**
 * Post-payment flow: confirm for ~2 seconds, then send the student straight to
 * the "Set your password" screen (no temporary password, no inbox round-trip).
 */
export default function PaymentSuccessPage() {
  const nav = useNavigate();
  const [purchase, setPurchase] = useState(null);

  useEffect(() => {
    let parsed = null;
    try {
      parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
    } catch {
      parsed = null;
    }
    setPurchase(parsed);

    const t = setTimeout(() => {
      if (parsed?.activation_token) {
        nav(`/student/set-password?token=${encodeURIComponent(parsed.activation_token)}`, {
          replace: true,
        });
        return;
      }
      const email = parsed?.email ? `?email=${encodeURIComponent(parsed.email)}` : "";
      nav(`/student/login${email}`, { replace: true });
    }, 2000);
    return () => clearTimeout(t);
  }, [nav]);

  return (
    <main
      data-testid="payment-success-page"
      className="min-h-screen bg-[#0F172A] text-white grid place-items-center px-6"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md text-center"
      >
        <motion.div
          initial={{ scale: 0.6 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 14 }}
          className="mx-auto h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 grid place-items-center"
        >
          <CheckCircle2 className="h-8 w-8 text-emerald-400" />
        </motion.div>

        <h1
          data-testid="success-headline"
          className="mt-6 font-display text-3xl sm:text-4xl font-semibold tracking-tighter"
        >
          Payment successful
        </h1>
        <p className="mt-3 text-sm text-slate-300 leading-relaxed">
          {purchase?.package_name ? `Your ${purchase.package_name} is active. ` : ""}
          {purchase?.activation_token
            ? "One last step — choose your password and you're in."
            : "Your account is ready."}{" "}
          {purchase?.email && (
            <span className="text-white font-medium">{purchase.email}</span>
          )}
        </p>

        <div
          data-testid="success-redirect-note"
          className="mt-7 inline-flex items-center gap-2 text-xs text-slate-400"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />{" "}
          {purchase?.activation_token
            ? "Taking you to account setup…"
            : "Taking you to the login page…"}
        </div>
      </motion.div>
    </main>
  );
}
